import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import LoadingBar from "react-top-loading-bar";
import { toast } from "react-toastify";
import { AdminPageTemplate, AuthorizeByPhoneModal, ColumnToolTip, ModalConfirmDelete } from "../../features";
import { PaginationSettings, topLoaderProgress } from "../../configs";
import { useFunctionalityAuthorizeByPhone } from "../hooks";
import { VrPhoneAssignmentResponse } from "../../models";
import { AssetSvg } from "../../assets";
import { formatUSPhoneNumber } from "../../utils/phoneNumberFormatting";

export function PageAuthorizeByPhone(): JSX.Element {
  const [loaderProgress, setLoaderProgress] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [createModalShow, setCreateModalShow] = useState(false);
  const [pageIndex, setPageIndex] = useState(PaginationSettings.initialPageIndex);
  const [pageSize, setPageSize] = useState(PaginationSettings.initialPageSize);
  const [tableData, setTableData] = useState<VrPhoneAssignmentResponse[]>([]);
  const [count, setCount] = useState(0);
  const [hasPagingData, setHasPagingData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editAssignment, setEditAssignment] = useState<VrPhoneAssignmentResponse | null>(null);
  const [assignmentToDelete, setAssignmentToDelete] = useState<VrPhoneAssignmentResponse | null>(null);

  const { onGetPagedPhoneAssignments, onPatchEnabledPhoneAssignment, onDeletePhoneAssignment } = useFunctionalityAuthorizeByPhone();
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());

  const handleCreateModalShow = (value: boolean) => {
    setEditAssignment(null);
    setCreateModalShow(value);
  };

  const handleModalShow = (value: boolean) => {
    setCreateModalShow(value);
    if (!value) {
      setEditAssignment(null);
    }
  };

  const handleDelete = () => {
    const targetId = assignmentToDelete?.id;
    if (!targetId) {
      return;
    }
    onDeletePhoneAssignment(targetId)
      .then((res) => {
        if (res?.isSuccess === false) {
          throw new Error(res?.message || "Failed to delete phone authorization");
        }
        setAssignmentToDelete(null);
        setTableData((prev) => prev.filter((item) => item.id !== targetId));
        toast.success("Phone authorization deleted");
      })
      .catch(() => {
        toast.error("Failed to delete phone authorization");
      });
  };

  const tableColumns = [
    {
      name: <ColumnToolTip data="User" />,
      selector: (row: VrPhoneAssignmentResponse) => {
        return <ColumnToolTip data={row.userFullName ?? row.userEmail ?? "-"} />;
      },
    },
    {
      name: <ColumnToolTip data="TIN" />,
      selector: (row: VrPhoneAssignmentResponse) => {
        return <ColumnToolTip data={row.tinNumber ?? "-"} />;
      },
    },
    {
      name: <ColumnToolTip data="Phone Number" />,
      selector: (row: VrPhoneAssignmentResponse) => {
        const rawValue = row.phoneNumberNormalized ?? row.phoneNumberRaw ?? "";
        return <ColumnToolTip data={formatUSPhoneNumber(rawValue) || "-"} />;
      },
    },
    {
      name: <ColumnToolTip data="Language Name" />,
      selector: (row: VrPhoneAssignmentResponse) => {
        return <ColumnToolTip data={row.languageName ?? "-"} />;
      },
    },
    {
      name: <ColumnToolTip data="Company" />,
      selector: (row: VrPhoneAssignmentResponse) => {
        return <ColumnToolTip data={row.companyName ?? "-"} />;
      },
    },
    {
      name: <ColumnToolTip data="Enabled" />,
      selector: (row: VrPhoneAssignmentResponse) => {
        const isUpdating = updatingIds.has(row.id);
        return (
          <input
            type="checkbox"
            checked={row.isEnabled}
            disabled={isUpdating}
            onChange={() => {
              if (isUpdating) {
                return;
              }
              const nextValue = !row.isEnabled;
              setUpdatingIds((prev) => new Set(prev).add(row.id));
              setTableData((prev) =>
                prev.map((item) =>
                  item.id === row.id ? { ...item, isEnabled: nextValue } : item
                )
              );
              onPatchEnabledPhoneAssignment(row.id, nextValue)
                .then((res) => {
                  if (res?.isSuccess === false) {
                    throw new Error(res?.message || "Failed to update phone assignment");
                  }
                  toast.success("Phone authorization updated");
                })
                .catch(() => {
                  setTableData((prev) =>
                    prev.map((item) =>
                      item.id === row.id ? { ...item, isEnabled: row.isEnabled } : item
                    )
                  );
                  toast.error("Failed to update phone authorization");
                })
                .finally(() => {
                  setUpdatingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(row.id);
                    return next;
                  });
                });
            }}
          />
        );
      },
    },
    {
      name: <ColumnToolTip data="Actions" />,
      allowOverflow: true,
      selector: (row: VrPhoneAssignmentResponse) => {
        return (
          <>
            <div
              className="btn svg-color"
              title="edit"
              onClick={() => {
                setEditAssignment(row);
                setCreateModalShow(true);
              }}
            >
              <span>
                <AssetSvg.Edit />
              </span>
            </div>
            <div
              className="btn svg-color"
              title="delete"
              onClick={() => {
                if (row.id) {
                  setAssignmentToDelete(row);
                }
              }}
            >
              <span>
                <AssetSvg.Delete />
              </span>
            </div>
          </>
        );
      },
    },
  ];

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    ReactDOM.unstable_batchedUpdates(() => {
      setSearchText(e.target.value);
      setPageIndex(1);
    });
  };

  const onChangePageIndex = (index: number) => {
    setPageIndex(index);
  };

  const onChangePageSize = (size: number) => {
    setPageSize(size);
  };

  const loadAssignments = useCallback(() => {
    let isActive = true;

    setLoading(true);
    setLoaderProgress(topLoaderProgress.start);
    onGetPagedPhoneAssignments(pageSize, pageIndex, searchText?.trimStart())
      .then((res) => {
        if (!isActive) {
          return;
        }
        const payload = res?.data ?? res;
        const dataList = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
        setTableData(dataList as VrPhoneAssignmentResponse[]);
        setCount(typeof payload?.count === "number" ? payload.count : dataList.length);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setTableData([]);
        setCount(0);
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setLoaderProgress(topLoaderProgress.complete);
        setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [onGetPagedPhoneAssignments, pageIndex, pageSize, searchText]);

  useEffect(() => {
    const cleanup = loadAssignments();
    return () => {
      cleanup?.();
    };
  }, [loadAssignments]);

  useEffect(() => {
    setHasPagingData(tableData.length > 0);
  }, [tableData]);

  return (
    <>
      <LoadingBar
        progress={loaderProgress}
        onLoaderFinished={() => setLoaderProgress(0)}
      />
      <AdminPageTemplate
        pagingKey="AuthorizeByPhone"
        loaderProgress={loaderProgress}
        pageTitle="Authorize By Phone"
        modalControlBtnLabel="Add Phone Authorization"
        setCreateModalShow={handleCreateModalShow}
        tableHeaders={tableColumns}
        tableData={tableData}
        searchText={searchText}
        handleSearch={handleSearch}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        count={count}
        onChangePageIndex={onChangePageIndex}
        onChangePageSize={onChangePageSize}
        hasPagingData={hasPagingData}
        setHasPagingData={setHasPagingData}
        loading={loading}
        hideSearchBar={false}
      />
      <AuthorizeByPhoneModal
        show={createModalShow}
        handleShow={handleModalShow}
        editAssignment={editAssignment}
        onSave={loadAssignments}
      />
      <ModalConfirmDelete
        show={assignmentToDelete !== null}
        handleShow={(show) => {
          if (!show) {
            setAssignmentToDelete(null);
          }
        }}
        deleteHandler={handleDelete}
        itemName={assignmentToDelete ? [
          assignmentToDelete.userFullName || assignmentToDelete.userEmail,
          formatUSPhoneNumber(assignmentToDelete.phoneNumberNormalized ?? assignmentToDelete.phoneNumberRaw ?? ""),
        ].filter(Boolean).join(" - ") || "Phone authorization" : undefined}
      />
    </>
  );
}
